import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Input, 
  Select, 
  Modal,
  Form,
  message,
  Typography,
  Tooltip,
  Popconfirm,
  Descriptions,
  List,
  InputNumber,
  Switch,
  Row,
  Col
} from 'antd';
import { 
  SettingOutlined,
  PlusOutlined, 
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  StarOutlined,
  StarFilled,
  CopyOutlined,
  PlusCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

const TaskTemplateManagement = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  
  const [searchText, setSearchText] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('');
  const [isDefaultFilter, setIsDefaultFilter] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form] = Form.useForm();

  // 获取模板列表
  const { data: templatesData, isLoading } = useQuery(
    ['templates', searchText, projectTypeFilter, isDefaultFilter],
    () => axios.get('/templates', {
      params: {
        search: searchText || undefined,
        projectType: projectTypeFilter || undefined,
        isDefault: isDefaultFilter || undefined,
        limit: 100
      }
    }).then(res => res.data.data),
    { refetchInterval: 30000 }
  );

  // 创建/更新模板
  const templateMutation = useMutation(
    (templateData) => {
      if (editingTemplate) {
        return axios.put(`/templates/${editingTemplate.id}`, templateData);
      } else {
        return axios.post('/templates', templateData);
      }
    },
    {
      onSuccess: () => {
        message.success(editingTemplate ? '模板更新成功' : '模板创建成功');
        setIsModalVisible(false);
        setEditingTemplate(null);
        // 延迟重置表单，确保Modal完全关闭后再重置
        setTimeout(() => {
          form.resetFields();
        }, 100);
        queryClient.invalidateQueries('templates');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || '操作失败');
      }
    }
  );

  // 删除模板
  const deleteTemplateMutation = useMutation(
    (templateId) => axios.delete(`/templates/${templateId}`),
    {
      onSuccess: () => {
        message.success('模板删除成功');
        queryClient.invalidateQueries('templates');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || '删除失败');
      }
    }
  );

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsModalVisible(true);
    // 延迟设置表单值，确保Modal完全打开后再设置
    setTimeout(() => {
      form.resetFields();
      form.setFieldsValue({
        taskList: [{ title: '', description: '', priority: 'medium', estimatedHours: 8 }],
        estimatedDays: 30,
        isDefault: false,
        status: 'active'
      });
    }, 100);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsModalVisible(true);
    // 延迟设置表单值，确保Modal完全打开后再设置
    setTimeout(() => {
      form.setFieldsValue({
        ...template,
        taskList: template.taskList || [{ title: '', description: '', priority: 'medium', estimatedHours: 8 }]
      });
    }, 100);
  };

  const handleDeleteTemplate = (template) => {
    deleteTemplateMutation.mutate(template.id);
  };

  /**
   * 复制模板处理函数
   * @param {Object} template - 要复制的模板对象
   */
  const handleCopyTemplate = (template) => {
    // 创建模板副本，去除id并修改名称
    const templateCopy = {
      ...template,
      name: `${template.name} - 副本`,
      isDefault: false, // 副本不能是默认模板
      createdAt: undefined,
      updatedAt: undefined,
      creator: undefined,
      creatorId: undefined
    };
    
    // 删除id字段，让后端自动生成新的id
    delete templateCopy.id;
    
    // 设置为编辑模式并打开模态框
    setEditingTemplate(null); // 设为null表示创建新模板
    setIsModalVisible(true);
    
    // 延迟设置表单值，确保Modal完全打开后再设置
    setTimeout(() => {
      form.setFieldsValue(templateCopy);
    }, 100);
  };

  /**
   * 在指定位置插入新任务的处理函数
   * @param {number} insertIndex - 要插入的位置索引
   * @param {Function} add - Form.List的add函数
   */
  const handleInsertTask = (insertIndex, add) => {
    const currentTaskList = form.getFieldValue('taskList') || [];
    
    // 创建新任务
    const newTask = { 
      title: '', 
      description: '', 
      priority: 'medium', 
      estimatedHours: 8 
    };
    
    // 在指定位置插入新任务
    const updatedTaskList = [
      ...currentTaskList.slice(0, insertIndex),
      newTask,
      ...currentTaskList.slice(insertIndex)
    ];
    
    // 更新后续任务的前置任务编号
    const adjustedTaskList = updatedTaskList.map((task, index) => {
      if (task.preTaskIndex && task.preTaskIndex >= insertIndex + 1) {
        // 如果前置任务编号大于等于插入位置，需要加1
        return {
          ...task,
          preTaskIndex: task.preTaskIndex + 1
        };
      }
      return task;
    });
    
    // 更新表单值
    form.setFieldsValue({ taskList: adjustedTaskList });
  };

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setIsDetailModalVisible(true);
  };

  const handleModalOk = () => {
    form.validateFields().then(values => {
      templateMutation.mutate(values);
    });
  };

  const getProjectTypeText = (type) => {
    const typeMap = {
      bidding: '投标项目',
      application: '申报项目',
      deepening: '深化设计',
      consultation: '咨询项目',
      other: '其他'
    };
    return typeMap[type] || type;
  };

  const getProjectTypeColor = (type) => {
    const colorMap = {
      bidding: 'red',
      application: 'orange',
      deepening: 'blue',
      consultation: 'green',
      other: 'default'
    };
    return colorMap[type] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'red',
      medium: 'orange',
      low: 'green'
    };
    return colors[priority] || 'default';
  };

  const getPriorityText = (priority) => {
    const texts = {
      high: '高',
      medium: '中',
      low: '低'
    };
    return texts[priority] || priority;
  };

  const columns = [
    {
      title: '模板名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.isDefault && (
            <StarFilled style={{ color: '#faad14', marginLeft: '8px' }} />
          )}
        </div>
      )
    },
    {
      title: '项目类型',
      dataIndex: 'projectType',
      key: 'projectType',
      render: (type) => (
        <Tag color={getProjectTypeColor(type)}>
          {getProjectTypeText(type)}
        </Tag>
      )
    },
    {
      title: '任务数量',
      dataIndex: 'taskList',
      key: 'taskCount',
      render: (taskList) => taskList?.length || 0
    },
    {
      title: '预估工期',
      dataIndex: 'estimatedDays',
      key: 'estimatedDays',
      render: (days) => `${days}天`
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      key: 'creator',
      render: (creator) => creator?.realName || '未知'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewTemplate(record)}
          />
          {['admin', 'manager'].includes(currentUser?.role) && (
            <>
              <Button 
                type="text" 
                icon={<CopyOutlined />} 
                onClick={() => handleCopyTemplate(record)}
                title="复制模板"
              />
              <Button 
                type="text" 
                icon={<EditOutlined />} 
                onClick={() => handleEditTemplate(record)}
              />
              <Popconfirm
                title="确认删除"
                description={`确定要删除模板"${record.name}"吗？`}
                onConfirm={() => handleDeleteTemplate(record)}
                okText="确认"
                cancelText="取消"
              >
                <Button 
                  type="text" 
                  danger
                  icon={<DeleteOutlined />} 
                />
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <SettingOutlined />
            常用任务模板管理
          </Space>
        }
        extra={
          ['admin', 'manager'].includes(currentUser?.role) && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreateTemplate}
            >
              新建模板
            </Button>
          )
        }
        className="status-card"
      >
        {/* 搜索和筛选 */}
        <div style={{ marginBottom: '16px' }}>
          <Space wrap>
            <Search
              placeholder="搜索模板名称或描述"
              allowClear
              style={{ width: 250 }}
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              placeholder="项目类型"
              allowClear
              style={{ width: 150 }}
              onChange={setProjectTypeFilter}
            >
              <Option value="bidding">投标项目</Option>
              <Option value="application">申报项目</Option>
              <Option value="deepening">深化设计</Option>
              <Option value="consultation">咨询项目</Option>
              <Option value="other">其他</Option>
            </Select>
            <Select
              placeholder="默认模板"
              allowClear
              style={{ width: 120 }}
              onChange={setIsDefaultFilter}
            >
              <Option value="true">默认模板</Option>
              <Option value="false">普通模板</Option>
            </Select>
          </Space>
        </div>

        {/* 模板表格 */}
        <Table
          columns={columns}
          dataSource={templatesData?.templates || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            total: templatesData?.pagination?.total,
            pageSize: templatesData?.pagination?.limit,
            current: templatesData?.pagination?.page,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个模板`
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 创建/编辑模板模态框 */}
      <Modal
        title={editingTemplate ? '编辑模板' : '新建模板'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingTemplate(null);
          // 延迟重置表单，确保Modal完全关闭后再重置
          setTimeout(() => {
            form.resetFields();
          }, 100);
        }}
        width={1400}
        destroyOnHidden
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            priority: 'medium',
            estimatedDays: 30,
            isDefault: false,
            status: 'active'
          }}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="name"
                label="模板名称"
                rules={[{ required: true, message: '请输入模板名称' }]}
              >
                <Input placeholder="请输入模板名称" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="projectType"
                label="项目类型"
                rules={[{ required: true, message: '请选择项目类型' }]}
              >
                <Select placeholder="选择项目类型">
                  <Option value="bidding">投标项目</Option>
                  <Option value="application">申报项目</Option>
                  <Option value="deepening">深化设计</Option>
                  <Option value="consultation">咨询项目</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="estimatedDays"
                label="预估工期（天）"
                rules={[{ required: true, message: '请输入预估工期' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="isDefault"
                label="设为默认模板"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name="status"
                label="状态"
              >
                <Select>
                  <Option value="active">启用</Option>
                  <Option value="inactive">禁用</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={3} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '24px' }}>
              <Space>
                <Button onClick={() => {
                  setIsModalVisible(false);
                  setEditingTemplate(null);
                  setTimeout(() => {
                    form.resetFields();
                  }, 100);
                }}>
                  取消
                </Button>
                <Button 
                  type="primary" 
                  loading={templateMutation.isLoading}
                  onClick={handleModalOk}
                >
                  确认
                </Button>
              </Space>
            </Col>
          </Row>

          <Form.Item
            label="任务清单"
            required
          >
            <Form.List
              name="taskList"
              rules={[
                {
                  validator: async (_, taskList) => {
                    if (!taskList || taskList.length < 1) {
                      return Promise.reject(new Error('至少需要一个任务'));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  <Table
                    dataSource={fields.map(({ key, name, ...restField }, index) => ({
                      key,
                      name,
                      index: index + 1,
                      ...restField
                    }))}
                    pagination={false}
                    size="small"
                    bordered
                    columns={[
                      {
                        title: '序号',
                        dataIndex: 'index',
                        width: 60,
                        align: 'center',
                      },
                      {
                        title: '任务标题',
                        dataIndex: 'title',
                        width: 200,
                        render: (_, record) => (
                          <Form.Item
                            name={[record.name, 'title']}
                            rules={[{ required: true, message: '请输入任务标题' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input placeholder="请输入任务标题" size="small" />
                          </Form.Item>
                        ),
                      },
                      {
                        title: '优先级',
                        dataIndex: 'priority',
                        width: 120,
                        render: (_, record) => (
                          <Form.Item
                            name={[record.name, 'priority']}
                            initialValue="medium"
                            style={{ marginBottom: 0 }}
                          >
                            <Select size="small">
                              <Option value="high">高</Option>
                              <Option value="medium">中</Option>
                              <Option value="low">低</Option>
                            </Select>
                          </Form.Item>
                        ),
                      },
                      {
                        title: '预估工时',
                        dataIndex: 'estimatedHours',
                        width: 100,
                        render: (_, record) => (
                          <Form.Item
                            name={[record.name, 'estimatedHours']}
                            initialValue={8}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber 
                              min={0} 
                              step={0.5} 
                              size="small" 
                              style={{ width: '100%' }} 
                            />
                          </Form.Item>
                        ),
                      },
                      {
                        title: '前置任务',
                        dataIndex: 'preTaskIndex',
                        width: 150,
                        render: (_, record) => (
                          <Form.Item
                            name={[record.name, 'preTaskIndex']}
                            style={{ marginBottom: 0 }}
                          >
                            <Select
                              allowClear
                              placeholder="无前置"
                              size="small"
                            >
                              {(() => {
                                const taskList = (form.getFieldValue('taskList') || []);
                                return taskList
                                  .map((task, idx) => ({
                                    index: idx + 1,
                                    title: task?.title || `任务 ${idx + 1}`
                                  }))
                                  .filter(opt => opt.index !== (record.index)) // 排除自身，避免形成自依赖
                                  .map(opt => (
                                    <Option key={opt.index} value={opt.index}>{`任务 ${opt.index} - ${opt.title}`}</Option>
                                  ));
                              })()}
                            </Select>
                          </Form.Item>
                        ),
                      },
                      {
                        title: '任务描述',
                        dataIndex: 'description',
                        render: (_, record) => (
                          <Form.Item
                            name={[record.name, 'description']}
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea 
                              placeholder="请输入任务描述" 
                              rows={1} 
                              size="small"
                            />
                          </Form.Item>
                        ),
                      },
                      {
                        title: '操作',
                        width: 120,
                        render: (_, record) => (
                          <Space size="small">
                            <Button
                              type="text"
                              icon={<PlusCircleOutlined />}
                              onClick={() => handleInsertTask(record.name, add)}
                              title="在此任务前插入新任务"
                              size="small"
                            />
                            {fields.length > 1 && (
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(record.name)}
                                title="删除此任务"
                                size="small"
                              />
                            )}
                          </Space>
                        ),
                      },
                    ]}
                  />
                  <div style={{ marginTop: 16 }}>
                    <Button
                      type="dashed"
                      onClick={() => add({ title: '', description: '', priority: 'medium', estimatedHours: 8 })}
                      block
                      icon={<PlusOutlined />}
                    >
                      添加任务
                    </Button>
                    <Form.ErrorList errors={errors} />
                  </div>
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>

      {/* 模板详情模态框 */}
      <Modal
        title="模板详情"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedTemplate && (
          <div>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="模板名称">
                <Space>
                  {selectedTemplate.name}
                  {selectedTemplate.isDefault && (
                    <StarFilled style={{ color: '#faad14' }} />
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="项目类型">
                <Tag color={getProjectTypeColor(selectedTemplate.projectType)}>
                  {getProjectTypeText(selectedTemplate.projectType)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="预估工期">
                {selectedTemplate.estimatedDays}天
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={selectedTemplate.status === 'active' ? 'green' : 'red'}>
                  {selectedTemplate.status === 'active' ? '启用' : '禁用'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="创建人">
                {selectedTemplate.creator?.realName || '未知'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(selectedTemplate.createdAt).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>
                {selectedTemplate.description || '无描述'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: '24px' }}>
              <Text strong>任务清单：</Text>
              <List
                style={{ marginTop: '16px' }}
                dataSource={selectedTemplate.taskList || []}
                renderItem={(task, index) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text>{index + 1}. {task.title}</Text>
                          <Tag color={getPriorityColor(task.priority)}>
                            {getPriorityText(task.priority)}
                          </Tag>
                          {task.estimatedHours && (
                            <Text type="secondary">({task.estimatedHours}h)</Text>
                          )}
                        </Space>
                      }
                      description={task.description}
                    />
                  </List.Item>
                )}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TaskTemplateManagement;